import { type Common } from '../../common.js';
import type * as PatientInsuranceInformation from '../../models/patient/insuranceInformation.js';
import {
  type ImportPatientOrder,
  type PatientOrder,
  type PatientOrderPopulated,
  type RawPatientOrder,
  type UpdateStatus,
} from '../../models/patient/order.js';
import { type ImportValidation } from '../../models/provider.js';

export class API {
  common: Common;
  baseEndpoint = '/patient-orders';

  constructor(common: Common) {
    this.common = common;
  }

  async upload(
    feedId: string,
    practiceId: string,
    orders: RawPatientOrder[],
    runFingerprintsImmediately: boolean
  ) {
    const url = this.common.constructPath(`${this.baseEndpoint}/sync/upload`);
    return await this.common.postJSON(url, {
      feedId,
      practiceId,
      orders,
      runFingerprintsImmediately,
    });
  }

  async importVerify(args: {
    feedId: string;
    practiceId: string;
    importRows: Record<string, string>[];
  }): Promise<Record<string, ImportValidation>[]> {
    const url = this.common.constructPath(`${this.baseEndpoint}/sync/import/verify`);
    return await this.common.postJSON(url, args);
  }

  async import(args: {
    feedId: string;
    feedUploadId?: string;
    practiceId: string;
    importRows: ImportPatientOrder[];
  }) {
    const url = this.common.constructPath(`${this.baseEndpoint}/sync/import`);
    return await this.common.postJSON(url, args);
  }

  async findAll(ehrInformationId: string): Promise<PatientOrder[]> {
    const url = this.common.constructPath(`${this.baseEndpoint}`, {
      ehrInformationId,
    });
    return await this.common.getJSON(url);
  }

  async findOne(id: string): Promise<PatientOrderPopulated> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${id}`);
    return await this.common.getJSON(url);
  }

  async findAllInsurance(id: string): Promise<PatientInsuranceInformation.InsuranceInformation[]> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${id}/insurances`);
    return await this.common.getJSON(url);
  }

  async findOneById(id: string): Promise<PatientOrderPopulated> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${id}`);
    return await this.common.getJSON(url);
  }

  async updateStatus(
    id: string,
    values: UpdateStatus
  ): Promise<UpdateStatus & { patientOrderId: string }> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${id}/authorization-statuses`);
    return await this.common.postJSON(url, values);
  }

  async patchStatus(id: string, values: { note?: string | null }) {
    const url = this.common.constructPath(`${this.baseEndpoint}/${id}/authorization-statuses`);
    return await this.common.patchJSON(url, values);
  }

  async update(id: string, values: Partial<PatientOrderPopulated>): Promise<PatientOrderPopulated> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${id}`);
    return await this.common.patchJSON(url, values);
  }

  async uploadScreenshot(
    id: string,
    body: Record<string, any>
  ): Promise<{ status: number; gsPath: string }> {
    const url = this.common.constructPath(`${this.baseEndpoint}/${id}/upload-screenshot`);
    return await this.common.postJSON(url, body);
  }
}
